import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { roomsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import socketService from '../services/socket';
import './WaitingRoom.css';

const WaitingRoom = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [roomInfo, setRoomInfo] = useState(null);
  const [hasRequestedAccess, setHasRequestedAccess] = useState(false);

  useEffect(() => {
    const storedName = sessionStorage.getItem(`waitingRoomName_${roomId}`);
    const hasRequested = sessionStorage.getItem(`waitingRoomRequested_${roomId}`);
    if (storedName) {
      setHasRequestedAccess(!!hasRequested);
    }
    
    loadRoomInfo();
    setupSocketListeners();

    return () => {
      socketService.off('waiting-room-approved', handleApproved);
      socketService.off('waiting-room-denied', handleDenied);
    };
  }, [roomId]);

  const loadRoomInfo = async () => {
    try {
      setLoading(true);
      try {
        const socketResponse = await socketService.getRoomInfo(roomId);
        setRoomInfo(socketResponse.room);
        setError('');
        
        const isHost = socketResponse.room?.hostId === user?.id;
        if (isHost) {
          navigate(`/room/${roomId}`);
          return;
        }
      } catch (socketErr) {
        try {
          const response = await roomsAPI.getRoom(roomId);
          setRoomInfo(response.data);
          setError('');
          
          const isHost = response.data?.isOwner || response.data?.isAdmin;
          if (isHost) {
            navigate(`/room/${roomId}`);
            return;
          }
        } catch (apiErr) {
          setError('Room not found');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const setupSocketListeners = () => {
    socketService.on('waiting-room-approved', handleApproved);
    socketService.on('waiting-room-denied', handleDenied);
  };

  const handleApproved = ({ room }) => {
    console.log('Approved to join room:', room);
    sessionStorage.removeItem(`waitingRoomName_${roomId}`);
    sessionStorage.removeItem(`waitingRoomRequested_${roomId}`);
    setTimeout(() => {
      navigate(`/room/${roomId}`);
    }, 1500);
  };

  const handleDenied = () => {
    console.log('Denied from joining room');
    sessionStorage.removeItem(`waitingRoomName_${roomId}`);
    sessionStorage.removeItem(`waitingRoomRequested_${roomId}`);
  };

  const handleJoinWaitingRoom = async () => {
    try {
      setLoading(true);
      const isHost = roomInfo?.isOwner || roomInfo?.isAdmin || roomInfo?.hostId === user?.id;
      if (isHost) {
        navigate(`/room/${roomId}`);
        return;
      }
      const name = user?.name || sessionStorage.getItem(`waitingRoomName_${roomId}`);
      if (!name) {
        setError('Nom requis');
        return;
      }
      await socketService.joinWaitingRoom(roomId, name);
      sessionStorage.setItem(`waitingRoomRequested_${roomId}`, 'true');
      setHasRequestedAccess(true);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    sessionStorage.removeItem(`waitingRoomName_${roomId}`);
    sessionStorage.removeItem(`waitingRoomRequested_${roomId}`);
    navigate('/');
  };

  if (authLoading || loading) {
    return (
      <div className="waiting-room-page">
        <div className="waiting-room-card">
          <div className="loading">Chargement...</div>
        </div>
      </div>
    );
  }

  if (error && !roomInfo) {
    return (
      <div className="waiting-room-page">
        <div className="waiting-room-card">
          <div className="error-icon">!</div>
          <h2>Erreur</h2>
          <p>{error}</p>
          <button className="btn-primary" onClick={() => navigate('/')}>
            Retour � l'accueil
          </button>
        </div>
      </div>
    );
  }

  const guestName = sessionStorage.getItem(`waitingRoomName_${roomId}`);

  // Guest user (not logged in) - waiting for approval
  if (!user && guestName && hasRequestedAccess) {
    return (
      <div className="waiting-room-page">
        <div className="waiting-room-card">
          <div className="waiting-icon"></div>
          <h2>Salle d'attente</h2>
          
          <div className="room-preview">
            <div className="room-name">{roomInfo?.name || 'R�union'}</div>
          </div>

          <div className="user-info">
            <p>Vous etes connectés en tant que:</p>
            <strong>{guestName}</strong>
          </div>

          <div className="waiting-message">
            <div className="spinner"></div>
            <p>En attente de l'approbation de l'hôte...</p>
            <p className="waiting-hint">L'hôte a été notifié de votre demande.</p>
          </div>
          
          <button className="btn-secondary" onClick={handleCancel}>Annuler</button>
        </div>
      </div>
    );
  }

  // Guest user - request form
  if (!user && guestName && !hasRequestedAccess) {
    return (
      <div className="waiting-room-page">
        <div className="waiting-room-card">
          <div className="waiting-icon"></div>
          <h2>Salle d'attente</h2>
          
          <div className="room-preview">
            <div className="room-name">{roomInfo?.name || 'R�union'}</div>
          </div>

          <div className="user-info">
            <p>Vous serez connectés en tant que:</p>
            <strong>{guestName}</strong>
          </div>

          <div className="join-actions">
            <button 
              className="btn-primary btn-large" 
              onClick={handleJoinWaitingRoom}
              disabled={loading}
            >
              {loading ? 'Demande...' : 'Demander l\'accès à la salle'}
            </button>
            <button className="btn-secondary" onClick={handleCancel}>Annuler</button>
          </div>
          
          {error && <div className="error-message">{error}</div>}
        </div>
      </div>
    );
  }

  // Logged in user
  if (user) {
    return (
      <div className="waiting-room-page">
        <div className="waiting-room-card">
          <div className="waiting-icon"></div>
          <h2>Salle d'attente</h2>
          
          <div className="room-preview">
            <div className="room-name">{roomInfo?.name || 'Reunion'}</div>
            {roomInfo?.description && (
              <p className="room-description">{roomInfo.description}</p>
            )}
          </div>

          <div className="user-info">
            <p>Vous etes connectés en tant que:</p>
            <strong>{user.name}</strong>
            <span>({user.email})</span>
          </div>

          {hasRequestedAccess ? (
            <div className="waiting-message">
              <div className="spinner"></div>
              <p>En attente de l'approbation de l'hôte...</p>
              <p className="waiting-hint">L'hôte a été notifié de votre demande.</p>
            </div>
          ) : (
            <div className="join-actions">
              <button 
                className="btn-primary btn-large" 
                onClick={handleJoinWaitingRoom}
                disabled={loading}
              >
                {loading ? 'Demande...' : 'Demander l\'accès à la salle'}
              </button>
              <button className="btn-secondary" onClick={handleCancel}>Annuler</button>
            </div>
          )}
          
          {error && <div className="error-message">{error}</div>}
        </div>
      </div>
    );
  }

  // No user and no guest name - redirect to enter name
  return (
    <div className="waiting-room-page">
      <div className="waiting-room-card">
        <div className="error-icon">!</div>
        <h2>Accès non autorisé</h2>
        <p>Veuillez d'abord entrer votre nom via le lien d'invitation.</p>
        <button className="btn-primary" onClick={() => navigate('/')}>
          Retour à l'accueil
        </button>
      </div>
    </div>
  );
};

export default WaitingRoom;
